import { useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, ShieldCheck, FileText } from "lucide-react";

const SUPPORT_EMAIL = "support@minemeanddo.com";

function Section({ id, Icon, title, children }) {
  return (
    <section id={id} data-testid={`about-section-${id}`} className="scroll-mt-24">
      <p className="label-mono text-[var(--text-2)] flex items-center gap-2">
        <Icon className="w-4 h-4 text-[var(--accent)]" /> {title}
      </p>
      <div className="mt-3 space-y-3 text-[var(--text-2)] leading-relaxed">{children}</div>
    </section>
  );
}

export default function AboutLegal() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <button
          data-testid="about-back"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-[var(--text-2)] hover:text-[var(--accent)] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mb-10">
          <p className="label-mono text-[var(--text-2)]">About &amp; Legal</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">
            ReMind Me <span className="italic text-[var(--accent)]">&amp; Do</span>
          </h1>
          <p className="text-[var(--text-2)] mt-3">
            Personal reminders and business field-service dispatch in one place. Questions? Email{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[var(--accent)] hover:underline">{SUPPORT_EMAIL}</a>.
          </p>
        </div>

        <div className="space-y-12">
          <Section id="about" Icon={Building2} title="About us">
            <p>
              ReMind Me &amp; Do helps individuals and small service businesses stay on top of what matters —
              daily reminders, bill tracking, savings goals, a 4-week SnapBudget matrix, and a unified Pulse
              calendar. Business users get a lightweight CRM with SMS, voice, and email dispatch to keep every
              client loop moving.
            </p>
            <p>
              We reply to every support message within <strong>24–48 hours</strong>.
            </p>
          </Section>

          <Section id="privacy" Icon={ShieldCheck} title="Privacy policy">
            <p>
              <em>This is a starter policy — please review and edit before public launch.</em>
            </p>
            <p>
              We collect only the information needed to operate the service: your account details, the reminders,
              tasks, budgets, and contacts you create, and basic usage data. We do not sell your personal data.
            </p>
            <p>
              Notifications you enable (SMS, voice, email) are delivered through our providers (Twilio and
              SendGrid) solely to send the messages you request. You can disable any channel at any time.
            </p>
            <p>
              Payment processing is handled by Stripe; we never store full card details on our servers. To request
              access to or deletion of your data, email <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[var(--accent)] hover:underline">{SUPPORT_EMAIL}</a>.
            </p>
          </Section>

          <Section id="terms" Icon={FileText} title="Terms of service">
            <p>
              <em>This is a starter agreement — please review and edit before public launch.</em>
            </p>
            <p>
              By using ReMind Me &amp; Do you agree to use the service lawfully and to only send notifications to
              recipients who have consented to receive them. You are responsible for the content of messages sent
              from your account.
            </p>
            <p>
              Paid plans renew automatically until cancelled. SnapBudget Premium includes a 14-day free trial; you
              can cancel anytime before it ends to avoid charges. The service is provided “as is” without warranty;
              we are not liable for missed reminders caused by carrier, network, or third-party outages.
            </p>
            <p>
              We may update these terms; continued use after changes constitutes acceptance. Questions about these
              terms can be sent to <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[var(--accent)] hover:underline">{SUPPORT_EMAIL}</a>.
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}
